import React, {useState,useMemo} from 'react';
import {Portal} from '../../../common/Portal.jsx';
import AgGrid from '../../../common/AgGrid/index.jsx';
import Dialog, {DialogHeader, DialogFooter} from '../../../bootstrap/Dialog.jsx';
import styles from '../ProjectPhasesBar.module.scss';
import TimelineSteps, {getPeriodKey, getPeriodLabel, getNextDate} from './TimelineSteps.jsx';
import { gridThemeNoBorder } from '../../../common/AgGrid/gridTheme.js';
import {
  project_phase_create_report_for_deliverable_sequence_entry_path,
  project_phase_mark_deliverable_sequence_entry_not_applicable_path,
  project_phase_redirect_to_report_for_deliverable_sequence_entry_path,
  project_report_edit_report_path,
} from '~/routes';

function DeliverableSequencesModal({handleCloseModal, project_id, recurrenceType, deliverable, deliverableSequences, updateDeliverableSequences}) {
  const [loading, setLoading] = useState(false);

  const handleInitiateReport = (scheduled_period) => {
    setLoading(true);

    jQuery.post(project_phase_create_report_for_deliverable_sequence_entry_path({project_id, deliverable_id: deliverable.id}), {scheduled_period})
      .done((res) => {
        //updateDeliverableSequences(deliverable.id, res);
        Turbo.visit(project_phase_redirect_to_report_for_deliverable_sequence_entry_path({project_id, scheduled_period, deliverable_id: deliverable.id}));
      })
      .fail((_res) => {});
  };

  const handleMarkNA = (scheduled_period) => {
    setLoading(true);

    jQuery.post(project_phase_mark_deliverable_sequence_entry_not_applicable_path({project_id, deliverable_id: deliverable.id}), {scheduled_period})
      .done((res) => {
        updateDeliverableSequences(deliverable.id, res);
        setLoading(false);
      })
      .fail((_res) => {});
  };

  const columnDefs = [
    {
      headerName: I18n.t('period'),
      field: 'scheduled_period',
      flex: 1,
      valueGetter: ({data}) => getPeriodLabel(recurrenceType, data.scheduled_period),
    },
    {
      headerName: I18n.t('status'),
      field: 'status',
      flex: 1,
      cellRenderer: ({data}) => {
        return (<>
          <span className={`${styles.status} ${styles.icon} ${styles[data.status]}`}></span>
          <span className={`${styles.status} ${styles[data.status]}`}>{I18n.t(data.status)}</span>
        </>);
      },
    },
    {
      headerName: I18n.t('action'),
      field: 'action',
      minWidth: 320,
      cellRenderer: ({data}) => {
        return (<>
          {data.status === 'available' ?
            <>
              <button disabled={loading} onClick={() => handleInitiateReport(data.scheduled_period)} className='btn btn-link m-0 p-0'>
                {I18n.t('initiate_draft')}
              </button>
              <span className={styles.verticalSeparator}/>
              <button disabled={loading} onClick={() => handleMarkNA(data.scheduled_period)} className='btn btn-link m-0 p-0'>
                {I18n.t('mark_as_not_applicable')}
              </button>
            </>
            : data.project_report_id && (
              <a href={project_report_edit_report_path({project_id, report_id: deliverable.monitoring_report_id, project_report_id: data.project_report_id})} target='_blank'>
                {I18n.t('view_report')}
              </a>
            )
          }
        </>)
      },
    }
  ];

  const isAutoHeight = useMemo(() => {
    return (deliverableSequences.length * 42 + 56) < 500  // (deliverableSequences.length * (ROW_HEIGHT) + HEADER_HEIGHT) < MAX_HEIGHT
  }, [deliverableSequences])

  const onFirstDataRendered = ({api}) => {
    // Scroll to current period
    api.forEachNode((node) => {
      if (['available', 'draft', 'in_review'].includes(node.data?.status)) {
        api.ensureNodeVisible(node, 'middle');
      }
    })
  };

  return (
    <Portal
      component={Dialog}
      header={<DialogHeader onClose={handleCloseModal} title={deliverable.title} showBorder={true} Tag='h4'/>}
      footer={<DialogFooter onClose={handleCloseModal} hideSaveButton={true}/>}
      hideSaveButton={true}
      size='lg'
    >
      <div>
        <div
          className={`grid-theme ${styles.sequencesAgGrid}`}
          style={{height: isAutoHeight ? 'auto' : `${500}px`}}
        >
          <AgGrid
            columnDefs={columnDefs}
            rowData={deliverableSequences}
            suppressContextMenu={true}
            defaultColDef={{suppressHeaderMenuButton: true}}
            domLayout={isAutoHeight ? 'autoHeight' : 'normal'}
            onFirstDataRendered={onFirstDataRendered}
            theme={gridThemeNoBorder}
          />
        </div>
      </div>
    </Portal>
  )
}

let isOpen = {};
const RecurringDeliverableCardContent = ({project_id, deliverable, shownPhase, updateDeliverableSequences = () => {}}) => {
  const {start_date: startDate, due_date: endDate} = shownPhase;
  const {reporting_frequency: recurrenceType, sequence_entries = []} = deliverable;

  const [collapsed, setCollapsed] = useState(true);
  const [openModal, setOpenModal] = useState(isOpen[deliverable.id]||false);

  const handleCloseModal = () => {
    isOpen[deliverable.id] = false;
    setOpenModal(false);
  };
  const handleOpenModal = () => {
    isOpen[deliverable.id] = true;
    setOpenModal(true);
  }

  const deliverableSequences = useMemo(() => {
    const startAt = dayjs(startDate);
    const endAt = dayjs(endDate);

    const allPeriods = [];
    let currentDate = startAt.clone();
    let activeFound = false;

    while (currentDate.isSameOrBefore(endAt)) {
      const periodKey = getPeriodKey(recurrenceType, currentDate); // Get key for this period
      const nextDate = getNextDate(recurrenceType, currentDate.clone());
      const currentSequenceEntry = sequence_entries.find(item => item.scheduled_period === periodKey);

      let status = currentSequenceEntry?.status;
      if (!(status === 'approved' || status === 'not_applicable')) {
        if (!activeFound) {
          status ||= 'available'; // first available
          activeFound = true;
        } else {
          status = 'locked'; // all next
        }
      }

      allPeriods.push({date: currentDate.clone(), scheduled_period: periodKey, ...currentSequenceEntry, status});
      currentDate = nextDate;
    }

    return allPeriods;
  }, [startDate, endDate, sequence_entries]);

  const timelineItems = useMemo(() => {
    const today = dayjs();
    const allPeriods = deliverableSequences;

    // Find current period index
    const currentIndex = allPeriods.findIndex((item, idx) => {
      if (['available', 'draft', 'in_review', 'approved', 'rejected'].includes(item.status)) {
        return true;
      }
      return false;
    })

    // If short timeline (<=8), just show everything
    if (allPeriods.length <= 8) {
      return allPeriods.map((item, index) => ({...item, type: 'dot'}));
    }

    // Pick which periods to display for longer timelines
    const lastIndex = allPeriods.length - 1;
    const visibleIndexes = [
      0, // first
      currentIndex - 1, // prev
      currentIndex, // current
      currentIndex + 1, // next
      currentIndex + 2, // next 2
      lastIndex, // last
    ]
      .filter((idx) => idx >= 0 && idx <= lastIndex)
      .sort((a, b) => a - b);

    const result = [];
    let previousIndex = -1;

    allPeriods.forEach((item, idx) => {
      if (collapsed && !visibleIndexes.includes(idx)) {
        // Only insert one ellipsis for consecutive hidden periods
        if (previousIndex !== 'ellipsis') {
          result.push({type: 'ellipsis'});
          previousIndex = 'ellipsis';
        }
        return;
      }
      // Add current period as a dot
      result.push({...item, type: 'dot'});
      previousIndex = idx;
    })

    return result;
  }, [deliverableSequences, collapsed]);

  return (
    <>
      <div onClick={handleOpenModal} className={`btn-link ${styles.Title}`} style={{cursor: 'pointer'}}>
        {deliverable.title}
      </div>

      <div className={styles.InfoRow}>
        <div className={styles.Icon} style={{marginTop: '-7px'}}>
          <i className={`fa-solid fa-clipboard ${styles[`${deliverable.type}-type-icon`]}`}>
            <i className={`fa-solid fa-repeat ${styles[`${deliverable.type}-type-icon`]}`} style={{fontSize: '10px', background: 'transparent', color: '#35d399', position: 'absolute', left: '5px'}}/>
          </i>
        </div>
        <TimelineSteps {...{recurrenceType, startDate, endDate, timelineItems, setCollapsed, collapsed}}/>
      </div>

      {openModal &&
        <DeliverableSequencesModal {...{handleCloseModal, project_id, recurrenceType, deliverable, deliverableSequences, updateDeliverableSequences}}/>
      }
    </>
  );
}

export default RecurringDeliverableCardContent;
